'use client';

import { useState } from 'react';
import { toast } from 'sonner';
import { Bell } from 'lucide-react';
import { Modal, BottomSheet } from '@paeffgen/react/crm-documents/ui';
import { useIsDesktop } from '@paeffgen/react/crm-documents/util';
import {
  NotificationFrequency,
  DocumentType,
} from '@paeffgen/react/crm-documents/data-access-graphql';
import { NotificationPrefsForm } from './NotificationPrefsForm';
import { useNotificationPrefs, useSetNotificationPrefs } from './use-notification-prefs';

const TITLE = 'Benachrichtigungen einrichten';

/**
 * Globaler Onboarding-Dialog für die Benachrichtigungseinstellungen. Wird
 * einmal in der authentifizierten Shell gemountet und erscheint nach dem
 * Login für Nutzer, die das Onboarding noch nicht abgeschlossen haben.
 *
 * "Später" blendet den Dialog nur für die aktuelle Session aus (kein
 * Mutation-Aufruf) — beim nächsten Login erscheint er erneut, solange
 * `onboardingDone` weiterhin false ist. "Speichern" persistiert die Auswahl,
 * wodurch der Dialog danach dauerhaft verschwindet.
 */
export function OnboardingDialog() {
  const { prefs, loading } = useNotificationPrefs();
  const { setPrefs, loading: saving } = useSetNotificationPrefs();
  const isDesktop = useIsDesktop();

  const [frequency, setFrequency] = useState<NotificationFrequency>(
    NotificationFrequency.INSTANT,
  );
  const [docTypes, setDocTypes] = useState<DocumentType[]>([DocumentType.RECHNUNG]);
  const [dismissed, setDismissed] = useState(false);

  if (loading || !prefs || prefs.disabledForAdmin || prefs.onboardingDone || dismissed) {
    return null;
  }

  const handleLater = () => {
    setDismissed(true);
  };

  const handleSave = async () => {
    try {
      await setPrefs(frequency, docTypes);
      toast.success('Benachrichtigungseinstellungen gespeichert.');
    } catch {
      toast.error('Benachrichtigungseinstellungen konnten nicht gespeichert werden.');
    }
  };

  const body = (
    <div className="space-y-5">
      <div className="flex items-start gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
          <Bell size={20} aria-hidden="true" />
        </span>
        <p className="text-sm text-gray-600">
          Erhalten Sie E-Mail-Benachrichtigungen, sobald neue Belege für Sie
          bereitstehen. Sie können diese Einstellungen jederzeit später unter
          „Einstellungen" anpassen.
        </p>
      </div>
      <NotificationPrefsForm
        frequency={frequency}
        docTypes={docTypes}
        onChange={(next) => {
          setFrequency(next.frequency);
          setDocTypes(next.docTypes);
        }}
      />
    </div>
  );

  const footer = (
    <div className="flex justify-end gap-3">
      <button
        type="button"
        onClick={handleLater}
        className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
      >
        Später
      </button>
      <button
        type="button"
        onClick={() => void handleSave()}
        disabled={saving}
        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-white bg-primary rounded-lg hover:bg-primary-hover shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Bell size={15} aria-hidden="true" />
        Speichern
      </button>
    </div>
  );

  return (
    <>
      <Modal
        isOpen={isDesktop}
        onClose={handleLater}
        title={TITLE}
        className="max-w-xl"
      >
        <div className="space-y-6">
          {body}
          {footer}
        </div>
      </Modal>

      <BottomSheet
        open={!isDesktop}
        onClose={handleLater}
        ariaLabel={TITLE}
        header={
          <div className="px-4 py-3">
            <h2 className="text-lg font-bold text-gray-900">{TITLE}</h2>
          </div>
        }
        footer={footer}
      >
        <div className="px-4 py-4">{body}</div>
      </BottomSheet>
    </>
  );
}
